import { Link } from 'react-router'
import type { JSX } from 'react'
import { api, call } from '@/api/client'
import type { Schema } from '@/api/client'
import { useQuery } from '@/api/query'
import { formatDate } from '@/lib/format'
import { Empty, ErrorState, Loading } from '@/ui/states'

type SpendData = Schema<'ModuleSpendOut'>
type Line = Schema<'SpendLineOut'>
type Cap = Schema<'SpendCapOut'>
type Module = SpendData['module']

/**
 * What each module calls itself on this panel.
 *
 * The only thing that differs between the outreach copy and the SEO copy, which
 * is why there is one component: every sentence about the *cap* is about the
 * organisation and reads identically on both screens, because there is one cap.
 */
const COPY: Record<Module, { label: string; nothingYet: string }> = {
  outreach: {
    label: 'Outreach',
    nothingYet:
      'Once discovery runs and messages start going out, this shows what outreach cost this month and how it sits against your organisation\u2019s budget cap.',
  },
  seo: {
    label: 'SEO',
    nothingYet:
      'Once audits, keyword research and content generation start running, this shows what SEO cost this month and how it sits against your organisation\u2019s budget cap.',
  },
  platform: {
    label: 'Platform',
    nothingYet:
      'Nothing has been metered against the platform this month. Service email and digests appear here when they are sent.',
  },
}

/**
 * Spend against cap — S-B31c's outreach panel and S-B32b's *Budget burn* card.
 *
 * **One component for two screens, because there is one cap.** S-B31c wrote this
 * for outreach and every argument below turned out to be about the product
 * rather than about outreach; `app/v1/spend.py` says the same thing about the
 * endpoint it reads. The module decides two sentences of copy and the path, and
 * nothing else.
 *
 * **The cap on this panel belongs to the organisation, and the panel says so out
 * loud.** There is no per-module ceiling in AugRank: the pre-flight in
 * `app/metering/service.py` checks one number for every module, so a meter drawn
 * as *"outreach: 41% of its cap"* would promise that outreach stops at 100%,
 * and it does not — it stops when the *organisation* does, which can happen with
 * outreach barely started because content generation spent the month. So the
 * meter is the organisation's, and outreach's figure sits beside it with its
 * share stated. `app/metering/attribution.py` carries the full argument.
 *
 * **The lines are not a column that adds up, and they are not drawn as one.**
 * Every figure is rounded up once over its own exact sum (`ledger.to_minor`), so
 * the lines can exceed the total by a paise apiece. Each therefore gets its own
 * share bar and there is no total row underneath — the same rule
 * `app/db/metering.meter_totals` already states for `admin#usage`.
 *
 * **Nothing here is computed.** Every rupee figure, percentage and reset date
 * arrives formatted from the endpoint, because the alternative is a second
 * implementation of money formatting in TypeScript that will disagree with the
 * invoice. `share` is `null` rather than `0` when the module spent nothing, and
 * renders as no bar rather than an empty one.
 *
 * **Its own query, like the other three.** A ledger read that fails leaves the
 * funnel, account health and deliverability on screen with their own data.
 */
export function SpendPanel({ module }: { module: Module }): JSX.Element {
  // Two literal paths rather than one interpolated string: the generated client
  // types each path against its own response, and a template literal would put
  // `never` on both sides of the union. They answer the same model, which is why
  // the branch ends here rather than running through the component.
  const spend = useQuery(
    (signal) =>
      module === 'seo'
        ? call(() => api.GET('/api/v1/seo/spend', { signal }))
        : call(() => api.GET('/api/v1/outreach/spend', { signal })),
    [module],
  )
  const copy = COPY[module]

  return (
    <section className="ar-stack">
      <h2 className="ar-h2">Spend against cap</h2>

      {spend.state.status === 'loading' ? (
        <Loading label={`Loading your ${copy.label.toLowerCase()} spend`} lines={3} />
      ) : null}

      {spend.state.status === 'error' ? (
        <div className="ar-card">
          <ErrorState
            failure={spend.state.failure}
            title="Could not load your spend"
            onRetry={spend.refetch}
          />
        </div>
      ) : null}

      {spend.state.status === 'ready' ? <Ready data={spend.state.data} module={module} /> : null}
    </section>
  )
}

function Ready({ data, module }: { data: SpendData; module: Module }): JSX.Element {
  if (data.empty) {
    return (
      <div className="ar-card">
        <Empty
          icon="₹"
          title="Nothing has been metered yet"
          body={COPY[module].nothingYet}
          action={
            <Link className="ar-btn ar-btn--ghost" to="/settings/budget">
              Set a budget cap
            </Link>
          }
        />
      </div>
    )
  }
  return (
    <>
      <div className="ar-card">
        <div className="ar-card__header">
          <h3>This month</h3>
          <span className="ar-card__spacer" />
          {/* `T00:00:00Z` appended, which is the spelling `UsagePage` already
              uses for this same `resets_on` field. `resets_on` is a bare
              `YYYY-MM-DD` and `new Date` of one is ambiguous; what matters more
              than the choice is that both screens make the *same* one, because
              two reset dates for one tenant is a worse bug than either. */}
          <span className="ar-subtle">Resets {formatDate(`${data.resets_on}T00:00:00Z`)}</span>
        </div>
        <div className="ar-card__body ar-stack">
          <Headline data={data} />
          <Ceiling cap={data.cap} />
          <Overage data={data} />
        </div>
      </div>
      <Lines data={data} />
    </>
  )
}

/**
 * What this module cost, and what share of the organisation's spend that is.
 *
 * Both numbers, always. The module's figure alone cannot answer the question a
 * tenant opens this panel with — *is this what is eating my cap* — and the share
 * is the only thing on the screen that does. The label comes from `data.module`
 * rather than from the prop, so the sentence always names the figures actually
 * being drawn.
 */
function Headline({ data }: { data: SpendData }): JSX.Element {
  const label = COPY[data.module].label
  return (
    <div className="app-spend__headline">
      <div>
        <div className="ar-muted app-spend__label">{label} this month</div>
        <div className="ar-h2 ar-num">{data.module_display}</div>
      </div>
      <div>
        <div className="ar-muted app-spend__label">All modules</div>
        <div className="ar-h2 ar-num">{data.tenant_display}</div>
        {/* An absent share rather than "0.0%": an organisation that has spent
            nothing has no split between its modules, and printing one would be
            a claim about where nothing went. */}
        <div className="ar-subtle app-spend__note">
          {data.share_of_tenant === null
            ? 'Nothing spent across the organisation yet'
            : `${label} is ${data.share_of_tenant.toFixed(1)}% of it`}
        </div>
      </div>
    </div>
  )
}

/**
 * The organisation's one ceiling, and where all its spending stands against it.
 *
 * Four states, and they are genuinely different sentences rather than one
 * sentence with a number swapped in:
 *
 * * **no cap set** — not a cap of zero. A tenant who has never opened the budget
 *   panel has no ceiling, and drawing an empty meter would imply they had spent
 *   all of one.
 * * **under it** — the meter, with what is left.
 * * **reached and paused** — automation has stopped, which is the only thing
 *   worth reading, so it leads.
 * * **reached and still running** — the switch is off, the tenant asked for
 *   this, and the overage below is what they will be billed.
 */
function Ceiling({ cap }: { cap: Cap }): JSX.Element {
  if (cap.effective_cap_minor === null) {
    return (
      <p className="ar-muted app-spend__nocap">
        No budget cap is set for this organisation, so nothing stops on spend.{' '}
        <Link to="/settings/budget">Set one in Settings</Link> to have automation pause before a
        month runs away.
      </p>
    )
  }
  return (
    <div className="ar-stack ar-stack--tight">
      <div className="ar-row app-spend__cap-head">
        <span>Organisation cap</span>
        <span className="ar-card__spacer" />
        <span className="ar-mono">
          {cap.effective_display}
          {cap.raised ? ' (raised this month)' : ''}
        </span>
      </div>
      <div
        className={`ar-meter ${cap.reached ? 'ar-meter--over' : ''}`}
        role="meter"
        aria-label={`Spend against the organisation's cap: ${String(cap.percent_used ?? 0)}%`}
        aria-valuenow={cap.percent_used ?? 0}
        aria-valuemin={0}
        aria-valuemax={100}
      >
        <div
          className="ar-meter__fill"
          style={{ width: `${String(Math.min(100, cap.percent_used ?? 0))}%` }}
        />
      </div>
      <p className="ar-subtle app-spend__note">
        <CapSentence cap={cap} />
      </p>
    </div>
  )
}

function CapSentence({ cap }: { cap: Cap }): JSX.Element {
  if (cap.paused) {
    return (
      <>
        <strong>Automation is paused</strong> — the organisation has reached its cap for this month.{' '}
        <Link to="/settings/budget">Raise it for this month</Link>, or wait for the period to roll
        over.
      </>
    )
  }
  if (cap.reached) {
    return (
      <>
        <strong>The cap has been reached and work is continuing</strong>, because pausing at the cap
        is switched off. Everything above the cap is billed as overage.
      </>
    )
  }
  return (
    <>
      {cap.remaining_display} left of {cap.effective_display} this month, across every module.
    </>
  )
}

/**
 * The overage line — S-B30's derivation, shown before the invoice exists.
 *
 * Deliberately organisation-wide and not apportioned to the module: the markup
 * is charged on what the *organisation* spent above its standing cap, and
 * splitting it across modules would be a number nobody invoices.
 */
function Overage({ data }: { data: SpendData }): JSX.Element | null {
  if (data.overage_due_minor === null) return null
  return (
    <p className="ar-subtle app-spend__overage">
      <span className="ar-badge ar-badge--warning">Overage</span> {data.overage_due_display} will be
      invoiced for this period — what the organisation spent above its standing cap, plus 20%.
    </p>
  )
}

/**
 * Where the module's money went, one row per source.
 *
 * Every line is shown, **including the ones that cost nothing**. Outreach
 * delivery is included via AugStash at ₹0 and an article is a plan allowance
 * rather than a charge — its cost is the tokens and images it consumed — so a
 * panel that hid them would tell a tenant who sent seven hundred messages, or
 * published twelve articles, that the module did nothing this month.
 */
function Lines({ data }: { data: SpendData }): JSX.Element {
  return (
    <div className="ar-card">
      <div className="ar-card__header">
        <h3>Where it went</h3>
      </div>
      <div className="ar-card__body ar-stack">
        {data.lines.map((line) => (
          <LineRow key={`${line.source}:${line.meter}`} line={line} module={data.module} />
        ))}
        <p className="ar-subtle app-spend__caption">
          Each figure is rounded up to the nearest paise on its own, so these can come to a paise or
          two more than the total above. Lines at ₹0 are part of your plan rather than a vendor
          charge, and are shown because they are work that happened.
        </p>
      </div>
    </div>
  )
}

function LineRow({ line, module }: { line: Line; module: Module }): JSX.Element {
  return (
    <div>
      <div className="ar-row app-spend__line-head">
        <span>{line.label}</span>
        <span className="ar-card__spacer" />
        <span className="ar-subtle app-spend__qty">
          {line.quantity.toLocaleString()} {line.unit}
          {line.quantity === 1 ? '' : 's'}
        </span>
        <span className="ar-mono app-spend__amount">{line.display}</span>
      </div>
      {/* No bar at all when there is nothing to be a share of — see `share`. */}
      {line.share === null ? null : (
        <div
          className="ar-meter"
          role="meter"
          aria-label={`${line.label}: ${String(line.share)}% of ${COPY[module].label.toLowerCase()} spend`}
          aria-valuenow={line.share}
          aria-valuemin={0}
          aria-valuemax={100}
        >
          <div
            className="ar-meter__fill"
            style={{ width: `${String(Math.min(100, line.share))}%` }}
          />
        </div>
      )}
    </div>
  )
}
